// Core
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useQuery } from 'react-query';
import {
  Button,
  Box,
  Grid,
  Flex,
  Text,
  FormControl,
  Input,
  useColorMode,
  useColorModeValue,
  useBreakpointValue
} from '@chakra-ui/react';

// Accets
import { apiService } from 'services/ApiService';
import { ReactComponent as EditIcon } from 'assets/svg/edit.svg';
import { ReactComponent as CancelIcon } from 'assets/svg/cancel.svg';

// Components
import Card from 'components/Card/Card';
import ErrorCard from 'components/Form/Errors';
import EditField from 'components/Form/EditField';
import EditSelect from 'components/Form/EditSelect';

const Address = ({ userData, userRefresh, setUserData }) => {
	const bg = useColorModeValue('white', '#0A1027');

	const { colorMode } = useColorMode();
    const isDark = colorMode === 'dark';

	const [object, setObject] = useState({
		billingCountry: userData.billingCountry,
		billingCity: userData.billingCity,
		billingAddress: userData.billingAddress,
		billingState: userData.billingState,
		billingPostcode: userData.billingPostcode
	});
	const [objectErrors, setObjectErrors] = useState([]);
	const [errorCard, setErrorCard] = useState();

	const setData = useCallback((maxValue, target, key) => {
		if (target.length < maxValue) {
		  setObjectErrors([...objectErrors, key]);
		} else {
		  setObjectErrors(objectErrors.filter(error => error !== key));
		}
		setObject({...object, [key]: target}); 
	}, [object, objectErrors, setObject, setObjectErrors]);

	const handleField = useMemo(() => {
		const fieldConfigs = [
		  { field: 'billingCountry', maxValue: 1 },
		  { field: 'billingCity', maxValue: 1 },
		  { field: 'billingState', maxValue: 0 },
		  { field: 'billingAddress', maxValue: 1 },
		  { field: 'billingPostcode', maxValue: 1 },
		];

		setErrorCard();

		return fieldConfigs.reduce((acc, { field, maxValue }) => {
		  acc[field] = (e) => setData(maxValue, e.target.value, field);
		  return acc;
		}, {});
	}, [setData]);

	const handleUpdateObject = useCallback(async () => {
		if (objectErrors.length > 0) {
	      setErrorCard(true);
	      return;
	    }

		try {
			const response = await apiService.updateUser(object);
			if(response.data === false || response.statusCode !== 200){
				setErrorCard(response.message);
				return;
			}

			setUserData(prev => ({
			  ...prev,
			  billingState: object.billingState
			}));
		} catch(e){
			setErrorCard(true);
			return;
		}

		setEditMode(false);
	});

	const [editMode, setEditMode] = useState(false);

	const isMobile = useBreakpointValue({ base: true, md: false });
 
	return (
		<Box mt="8px">
			{ errorCard && ( <ErrorCard error={errorCard} /> )}

			<Card padding={{base: '16px 12px 16px 12px', md: '16px 16px 44px 16px'}} bg={bg} overflow="hidden" style={ isDark ? {} : { boxShadow: 'unset'} }>
				<Flex alignItems="center" p="0 0 0 18px" mb="24px" justifyContent="space-between">
					<Text variant="title-lg" fontSize="18px" fontStyle="normal" fontWeight="800" letterSpacing="-1px" textTransform="uppercase">
						Address
					</Text>

					<>
						{ editMode ? (
							<Flex gap="12px">
								<Button onClick={() => setEditMode(!editMode)} variant="delete-md" w={{base: '44px', md: '88px'}}>{isMobile ? (<CancelIcon />) : 'Cancel'}</Button>
								<Button onClick={handleUpdateObject}  variant="button-md" w={{base: '72px', md: '124px'}}>Save</Button>
							</Flex>
						) : (
							<Button onClick={() => setEditMode(!editMode)} variant="solid-md" w="88px">Edit <EditIcon fontSize="24px" style={{marginLeft: '6px'}} /></Button>
						)}
					</>
				</Flex>

				<Grid mt="2px" alignItems="center" templateColumns={{ sm: '1fr', lg: '1fr 1fr' }} gap="16px" rowGap="44px" templateRows='1fr' zIndex="2">
			        <EditSelect 
			        	editMode={editMode}
			        	options={[
			        		{ value: 'AU', title: 'Australia' }
			        	]}
			        	id="country" value={object.billingCountry} label="Country" onChange={handleField.billingCountry} placeholder="Country" isValid={true}  />
			        <EditField editMode={editMode} id="billingCity" label="City" type="text" value={object.billingCity} isValid={true} onChange={handleField.billingCity} />
			        <EditSelect 
			        	editMode={editMode}
			        	options={[
			        	{ value: 'ACT', title: 'Australian Capital Territory' },
						    { value: 'NSW', title: 'New South Wales' },
						    { value: 'NT', title: 'Northern Territory' },
						    { value: 'SA', title: 'South Australia' },
						    { value: 'TAS', title: 'Tasmania' },
						    { value: 'VIC', title: 'Victoria' },
						    { value: 'QLD', title: 'Queensland' },
						    { value: 'WA', title: 'Western Australia' }
						]}
			        	id="state" value={object.billingState} label="State" onChange={handleField.billingState} placeholder="State" isValid={true}  />
			        <EditField editMode={editMode} id="billingAddress" label="Address" type="text" value={object.billingAddress} isValid={true} onChange={handleField.billingAddress} />
			        <EditField editMode={editMode} id="billingPostcode" label="Postcode" type="text" value={object.billingPostcode} isValid={true} onChange={handleField.billingPostcode} />
				</Grid>
			</Card>
		</Box>
	);
};


export default Address;